import React, { useEffect, useState, useMemo } from "react";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToCleaningData } from "../../../../redux/cleaningSlice";
import {
  listenToCleaningListingReservations,
  listenToCleaningReservationData,
} from "../../../../redux/cleaningReservationSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import axios from "axios";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight, AlertTriangle, XCircle } from "lucide-react";

/* ── green theme tokens (mirrors host cancel page) ── */
const T = {
  bg:          "#f0fdf4",
  surface:     "#ffffff",
  border:      "#bbf7d0",
  border2:     "#86efac",
  text:        "#14532d",
  textSoft:    "#166534",
  muted:       "#6b7280",
  green:       "#16a34a",
  greenLight:  "#dcfce7",
  greenBorder: "#4ade80",
  red:         "#C0392B",
  redLight:    "#FDEEEC",
  redBorder:   "#F5B7B1",
  sans:        "'Montserrat', sans-serif",
  serif:       "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300&family=Montserrat:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  input, textarea, button { font-family: 'Montserrat', sans-serif; }
`;

/* ── helpers ── */
const normalizeDateHelper = (date: any): Date | null => {
  if (!date) return null;
  if (date.seconds) return new Date(date.seconds * 1000);
  return new Date(date);
};

const formatDateHelper = (date: any): string => {
  const d = normalizeDateHelper(date);
  if (!d || isNaN(d.getTime())) return "—";
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

/* ════════════════════════════════════════════════════════ */
export default function CancelCleaningBooking() {
  const [open, setOpen] = useState(false);

  const { guestId: guestIdParam, listingId, reservationId } = useParams<{
    guestId: string;
    listingId: string;
    reservationId: string;
  }>();

  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();

  const { user }            = useSelector((s: RootState) => s.auth);
  const listing             = useSelector((s: RootState) => s.cleaning.currentListing);
  const reservation         = useSelector((s: RootState) => s.cleaningReservations.reservationById);
  const allUsers            = useSelector((s: RootState) => s.auth.allUsers);
  const listingReservations = useSelector((s: RootState) => s.cleaningReservations.listingReservations);

  const [showModal,          setShowModal]          = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [loading,            setLoading]            = useState(false);
  const [monthOffset,        setMonthOffset]        = useState(0);

  const guestId    = guestIdParam || user?.userId || "";
  const booking    = reservation;
  const isCancelled = reservation?.isCancelled === true;

  /* ── fetch data ── */
  useEffect(() => {
    if (listingId)     dispatch(listenToCleaningData(listingId));
    if (reservationId) dispatch(listenToCleaningReservationData(reservationId));
    if (listingId)     dispatch(listenToCleaningListingReservations(listingId));
    if (allUsers.length === 0) dispatch(fetchAllUsers());
  }, [dispatch, listingId, reservationId, allUsers.length]);

  /* auto-jump calendar to booking month */
  useEffect(() => {
    if (!booking) return;
    const checkIn = new Date(booking.checkIn);
    const today   = new Date();
    const diff =
      (checkIn.getFullYear() - today.getFullYear()) * 12 +
      (checkIn.getMonth()    - today.getMonth());
    setMonthOffset(Math.max(0, diff));
  }, [booking]);

  /* ── host info ── */
  const host = booking ? allUsers.find((u) => u.userId === booking.hostId) : null;

  /* ── calendar helpers ── */
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const currentViewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const currentMonth    = currentViewDate.toLocaleString("default", { month: "long" });
  const currentYear     = currentViewDate.getFullYear();

  const isPastMonth =
    currentViewDate.getFullYear() < today.getFullYear() ||
    (currentViewDate.getFullYear() === today.getFullYear() &&
      currentViewDate.getMonth() < today.getMonth());

  const blockedDates = new Set<string>(
    (listing?.blockedDuration || []).map((d: string) => new Date(d).toDateString())
  );

  const reservedDates = new Set<string>();
  listingReservations.forEach((res: any) => {
    if (res.isCancelled) return;
    const start = new Date(res.checkIn);
    const end   = new Date(res.checkOut);
    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1))
      reservedDates.add(new Date(d).toDateString());
  });

  const currentResStart = booking ? new Date(booking.checkIn)  : null;
  const currentResEnd   = booking ? new Date(booking.checkOut) : null;

  const getMonthDates = (): Date[] => {
    const firstDay = new Date(currentViewDate.getFullYear(), currentViewDate.getMonth(), 1);
    const startDay = (firstDay.getDay() + 6) % 7;
    const start    = new Date(firstDay);
    start.setDate(start.getDate() - startDay);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i); return d;
    });
  };

  /* ── derived ── */
  const days = currentResStart && currentResEnd
    ? Math.round((currentResEnd.getTime() - currentResStart.getTime()) / 86400000)
    : 0;

  const durationLabel = (() => {
    const rt = listing?.rateType;
    if (rt === "perHour")  return `${days * 8}h service`;
    if (rt === "perWeek")  return `${Math.ceil(days / 7)} week${Math.ceil(days / 7) > 1 ? "s" : ""}`;
    if (rt === "perMonth") return `${Math.ceil(days / 30)} month${Math.ceil(days / 30) > 1 ? "s" : ""}`;
    return `${days} day${days !== 1 ? "s" : ""}`;
  })();

  const totalDisplay = (() => {
    const raw = booking?.totalCharged ?? (booking as any)?.totalPaid;
    if (!raw) return "—";
    return `€${Number(raw).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
  })();

  /* ── cancel ── */
  const confirmCancel = async () => {
    if (isCancelled) return;
    if (!booking || !listing || !guestId) { alert("Missing information."); return; }
    setLoading(true);
    try {
      await axios.post("https://areluxe.com/applyCleaningRefund", {
        reservationId: booking.id,
        hostId:        booking.hostId,
        guestId,
        listingId,
        dateKey:           booking.checkIn,
        cancellationType:  "guest",
        cancellationReason,
      });
      navigate(`/cleaningbookings/${guestId}`);
    } catch (error: any) {
      alert(error?.response?.data?.message || "Failed to cancel booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── day cell ── */
  const renderDayCell = (date: Date, i: number) => {
    const dateStr      = date.toDateString();
    const isOtherMonth = date.getMonth() !== currentViewDate.getMonth();
    const isCheckIn    = !!currentResStart && dateStr === currentResStart.toDateString();
    const isCheckOut   = !!currentResEnd   && dateStr === currentResEnd.toDateString();
    const isInBooking  = !!currentResStart && !!currentResEnd &&
                         date > currentResStart && date < currentResEnd;
    const isToday      = dateStr === today.toDateString();
    const isPast       = date < today;

    if (isPastMonth && !isCheckIn && !isCheckOut && !isInBooking) {
      return (
        <div key={i} style={{ padding: "7px 2px 5px", textAlign: "center", borderRadius: 8, opacity: isOtherMonth ? 0.1 : 0.3 }}>
          <span style={{ fontSize: 12, color: T.border2 }}>{date.getDate()}</span>
        </div>
      );
    }

    let bg     = "transparent";
    let color  = T.text;
    let fontW  = 400 as number;
    let radius = "8px";
    let sub: React.ReactNode = null;

    if (isCheckIn) {
      bg = T.green; color = "#fff"; fontW = 600; radius = "8px 0 0 8px";
      sub = <span style={{ display: "block", fontSize: 7, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "rgba(255,255,255,.75)", marginTop: 2 }}>In</span>;
    } else if (isCheckOut) {
      bg = T.greenLight; color = T.green; fontW = 600; radius = "0 8px 8px 0";
      sub = <span style={{ display: "block", fontSize: 7, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: T.green, marginTop: 2 }}>Out</span>;
    } else if (isInBooking) {
      bg = T.greenLight; color = T.text; radius = "0";
    } else if (blockedDates.has(dateStr) || reservedDates.has(dateStr)) {
      color = T.border2;
    }

    if (isPast && !isCheckIn && !isCheckOut && !isInBooking) color = T.border2;

    return (
      <div key={i} style={{
        padding: "7px 2px 5px", textAlign: "center", borderRadius: radius,
        background: bg, opacity: isOtherMonth ? 0.2 : 1,
        outline: isToday && !isCheckIn && !isInBooking ? `2px solid ${T.greenBorder}` : "none",
        outlineOffset: -1,
      }}>
        <span style={{ fontSize: 12, fontWeight: fontW, color, lineHeight: 1 }}>{date.getDate()}</span>
        {sub}
      </div>
    );
  };

  /* ════════════════════ RENDER ════════════════════ */
  return (
    <div style={s.centerWrap}>
      <style>{FONT_CSS}</style>

      <aside style={s.stickyCard}>

        {/* 🧹 LISTING SUMMARY */}
        <section style={s.listingCard}>
          <div style={s.listingLeft}>
            <strong style={s.listingTitle}>{listing?.name || listing?.title || "Cleaning Service"}</strong>
            {listing?.address && (
              <span style={s.listingAddress}>
                {[listing.address.street, listing.address.city, listing.address.country].filter(Boolean).join(", ")}
              </span>
            )}
            {listing && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 4, background: T.greenLight, borderRadius: 999, padding: "3px 8px" }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: T.green }} />
                <span style={{ color: T.green, fontSize: 10, fontWeight: 700 }}>
                  {listing.isCompany ? "Professional Company" : "Individual Cleaner"}
                </span>
              </div>
            )}
          </div>
          <div style={s.listingImageWrap}>
            {listing?.photos?.[0]
              ? <img src={listing.photos[0]} alt="Listing" style={s.listingImage} />
              : <div style={s.imagePlaceholder} />
            }
          </div>
        </section>

        {/* 📅 BOOKING DATES */}
        <div style={s.inputCard}>
          <div style={s.grid2}>
            <div style={s.inputCell}>
              <div style={s.inputLabel}>SERVICE DATE</div>
              <div style={s.inputValue}>{booking ? formatDateHelper(booking.checkIn) : "—"}</div>
            </div>
            <div style={{ ...s.inputCell, borderLeft: `1px solid ${T.border}` }}>
              <div style={s.inputLabel}>END DATE</div>
              <div style={s.inputValue}>{booking ? formatDateHelper(booking.checkOut) : "—"}</div>
            </div>
          </div>
          <div style={s.guestRow}>
            <div style={s.inputLabel}>DURATION</div>
            <div style={s.inputValue}>{durationLabel}</div>
          </div>
        </div>

        {/* 📆 MINI CALENDAR */}
        <div style={s.calCard}>
          <div style={s.calHeader}>
            <button
              style={s.chevBtn}
              onClick={() => setMonthOffset((m) => Math.max(0, m - 1))}
              disabled={monthOffset === 0}
            >
              <ChevronLeft size={14} color={monthOffset === 0 ? T.border2 : T.green} />
            </button>
            <span style={s.calMonth}>{currentMonth} {currentYear}</span>
            <button style={s.chevBtn} onClick={() => setMonthOffset((m) => Math.min(18, m + 1))}>
              <ChevronRight size={14} color={T.green} />
            </button>
          </div>
          <div style={s.calGrid}>
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
              <div key={d} style={s.dayLabel}>{d}</div>
            ))}
            {getMonthDates().map((date, i) => renderDayCell(date, i))}
          </div>
          <div style={s.legend}>
            <div style={s.legendItem}><div style={{ ...s.legendDot, background: T.green }} /><span>Start</span></div>
            <div style={s.legendItem}><div style={{ ...s.legendDot, background: T.greenLight, border: `1px solid ${T.greenBorder}` }} /><span>Service period</span></div>
            <div style={s.legendItem}><div style={{ ...s.legendDot, background: T.border2 }} /><span>Past</span></div>
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {/* ── CANCEL BUTTON ── */}
        <button
          style={{
            ...s.cancelBtn,
            background: isCancelled ? "#ccc" : T.red,
            cursor: isCancelled ? "not-allowed" : "pointer",
            opacity: isCancelled ? 0.7 : 1,
          }}
          onClick={() => !isCancelled && setShowModal(true)}
          disabled={loading || isCancelled}
        >
          {isCancelled ? "ALREADY CANCELLED" : "CANCEL BOOKING"}
        </button>

        {/* ℹ️ POLICY NOTE */}
        <section style={s.card}>
          <div style={s.mutedText}>
            Read the Refund Policy before cancelling. If you cancel, you will lose your service fee.
            One day's rate may be paid to the cleaner per platform policy. The remaining balance will be refunded.
          </div>
        </section>

        {/* 📕 PAYMENT DETAILS */}
        <div style={s.bookCard}>
          <div style={s.bookHeader} onClick={() => setOpen((o) => !o)}>
            <strong>Payment details</strong>
            {open ? <ExpandLess /> : <ExpandMore />}
          </div>
          <motion.div
            initial={false}
            animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            style={s.whitePage}
          >
            <div style={s.pageTop} />
            <div style={s.pageContent}>
              <div style={s.sectionHeader}>Payment Summary</div>
              <div style={s.tableRow}>
                <span>You Paid</span>
                <span>{totalDisplay}</span>
              </div>
              <div style={s.sectionHeader}>You Will Lose</div>
              <div style={s.tableRow}>
                <span>Service Fee</span>
                <span>€{(booking?.guestsFee || 0).toFixed(2)}</span>
              </div>
              <div style={s.tableRow}>
                <span>VAT Collected</span>
                <span>€{((booking as any)?.vatCollected || 0).toFixed(2)}</span>
              </div>
              <div style={{ ...s.tableRow, fontWeight: 900, marginTop: 10, borderTop: "2px solid #eee", paddingTop: 10 }}>
                <span>Estimated Refund</span>
                <span>€{Math.max(0,
                  (booking?.totalPaid || 0) -
                  (booking?.guestsFee  || 0) -
                  ((booking as any)?.vatCollected || 0)
                ).toFixed(2)}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ════════ CANCEL MODAL ════════ */}
        {showModal && !isCancelled && (
          <div style={s.floatingOverlay}>
            <div style={s.floatingCard}>

              {/* red top bar */}
              <div style={{ height: 4, background: T.red, borderRadius: "16px 16px 0 0", margin: "-28px -28px 20px" }} />

              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: T.redLight, display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <XCircle size={18} color={T.red} />
                </div>
                <h3 style={{ fontSize: 16, fontFamily: T.serif, color: T.text, fontWeight: 500 }}>Confirm Cancellation</h3>
              </div>

              <div style={s.warningBox}>
                <AlertTriangle size={13} color={T.red} style={{ flexShrink: 0, marginTop: 2 }} />
                <p style={{ fontSize: 12, color: "#922B21", lineHeight: 1.55 }}>
                  If you cancel, you will lose your <strong>service fee</strong> and one day's rate
                  may be paid to the cleaner. The remaining balance will be refunded.
                </p>
              </div>

              <label style={s.textareaLabel}>
                Reason <span style={{ color: T.muted, fontWeight: 400 }}>(optional)</span>
              </label>
              <textarea
                placeholder="e.g. Schedule change, found another cleaner…"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                style={s.modalTextarea}
                rows={3}
              />

              <div style={s.modalButtonRow}>
                <button
                  style={{
                    flex: 2, padding: "12px 0", borderRadius: 10, border: "none",
                    background: T.red, color: "#fff", fontWeight: 700, fontSize: 13,
                    cursor: loading ? "not-allowed" : "pointer", fontFamily: T.sans,
                    opacity: loading ? 0.7 : 1,
                  }}
                  onClick={confirmCancel}
                  disabled={loading}
                >
                  {loading ? "Processing…" : "Yes, cancel booking"}
                </button>
                <button
                  style={{
                    flex: 1, padding: "12px 0", borderRadius: 10,
                    border: `1.5px solid ${T.border}`, background: "transparent",
                    color: T.textSoft, fontWeight: 600, fontSize: 12,
                    cursor: "pointer", fontFamily: T.sans,
                  }}
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Go back
                </button>
              </div>
            </div>
          </div>
        )}

      </aside>
    </div>
  );
}

/* ── styles ── */
const s: Record<string, React.CSSProperties> = {
  centerWrap:  { display: "flex", justifyContent: "center", padding: "40px 16px", background: T.bg, minHeight: "100vh", fontFamily: T.sans },
  stickyCard:  { width: "100%", maxWidth: 520, minWidth: 340, border: `1px solid ${T.border}`, borderRadius: 18, padding: 24, boxShadow: "0 10px 30px rgba(0,0,0,.08)", display: "flex", flexDirection: "column", gap: 12, background: T.surface },

  card:         { padding: 16, borderRadius: 16, border: `1px solid ${T.border}` },
  mutedText:    { fontSize: 12, color: T.muted, lineHeight: 1.6 },

  listingCard:     { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, padding: 16, borderRadius: 16, border: `1px solid ${T.border}` },
  listingLeft:     { display: "flex", flexDirection: "column", gap: 4, flex: 1 },
  listingTitle:    { fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.serif },
  listingAddress:  { fontSize: 12, color: T.muted },
  listingImageWrap:{ width: 64, height: 64, borderRadius: 12, overflow: "hidden", flexShrink: 0, boxShadow: "0 4px 10px rgba(0,0,0,.1)" },
  listingImage:    { width: "100%", height: "100%", objectFit: "cover" },
  imagePlaceholder:{ width: "100%", height: "100%", background: T.greenLight },

  inputCard:  { border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" },
  grid2:      { display: "grid", gridTemplateColumns: "1fr 1fr" },
  guestRow:   { borderTop: `1px solid ${T.border}`, padding: "10px 12px" },
  inputCell:  { padding: "12px 14px" },
  inputLabel: { fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: T.green, textTransform: "uppercase" as const },
  inputValue: { fontSize: 13, fontWeight: 600, marginTop: 3, color: T.text },

  calCard:   { border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 12px" },
  calHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  chevBtn:   { width: 28, height: 28, borderRadius: "50%", border: `1px solid ${T.border}`, background: T.bg, display: "grid", placeItems: "center", cursor: "pointer" },
  calMonth:  { fontSize: 13, fontWeight: 600, color: T.text },
  calGrid:   { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 },
  dayLabel:  { textAlign: "center" as const, fontSize: 9, fontWeight: 600, color: T.muted, padding: "3px 0 6px", letterSpacing: "0.04em" },
  legend:    { display: "flex", gap: 12, marginTop: 10, justifyContent: "center" },
  legendItem: { display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: T.muted },
  legendDot: { width: 8, height: 8, borderRadius: "50%" },

  cancelBtn: { width: "100%", padding: 14, border: "none", fontWeight: 700, fontSize: 14, color: "#fff", borderRadius: 12, letterSpacing: "0.04em" },

  bookCard:    { background: T.surface, border: `2px solid ${T.green}`, borderRadius: 16, padding: 20, boxShadow: `0 4px 16px rgba(22,163,74,.1)` },
  bookHeader:  { display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.text, fontSize: 14, fontWeight: 600 },
  whitePage:   { background: T.bg, borderRadius: 10, marginTop: 14, overflow: "hidden" },
  pageTop:     { height: 3, background: `repeating-linear-gradient(90deg,${T.border} 0 10px,${T.surface} 10px 20px)` },
  pageContent: { padding: "14px 16px" },
  tableRow:    { display: "flex", justifyContent: "space-between", fontSize: 13, color: T.text, padding: "7px 0", borderBottom: `1px dashed ${T.border}` },
  sectionHeader:{ marginTop: 14, marginBottom: 8, fontWeight: 700, fontSize: 11, color: T.green, letterSpacing: "0.08em", textTransform: "uppercase" as const },

  floatingOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.3)", backdropFilter: "blur(6px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 999 },
  floatingCard:    { background: T.surface, width: "92%", maxWidth: 420, borderRadius: 16, padding: 28, boxShadow: "0 25px 60px rgba(0,0,0,.2)", border: `1px solid ${T.border}` },
  warningBox:      { display: "flex", gap: 8, alignItems: "flex-start", background: T.redLight, border: `1px solid ${T.redBorder}`, padding: "12px 14px", borderRadius: 10, marginBottom: 14 },
  textareaLabel:   { display: "block", fontSize: 11, fontWeight: 600, color: T.textSoft, marginBottom: 6, letterSpacing: "0.04em" },
  modalTextarea:   { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.bg, fontSize: 13, color: T.text, resize: "vertical" as const, outline: "none", marginBottom: 14, lineHeight: 1.5 },
  modalButtonRow:  { display: "flex", gap: 10 },
};
